// CRM web shell — React Router v7 route tree.
//
// Layout tree:
//
//   /login          → LoginPage (no shell)
//   /terms /privacy /support → legal pages (no shell, public before auth)
//   *               → NotFoundPage (no shell)
//   /               → <Navigate to="/dashboard" />
//   <AppLayout>     ← persistent shell (AuthGuard + Sidebar + Topbar + Outlet)
//     /dashboard    → DashboardPage
//     /deals        → DealKanbanPage
//     /contacts     → ContactsPage
//     /templates    → TemplatesPage
//     /hot-leads    → HotLeadsPage
//     /settings     → SettingsPage
//
// Design notes:
//   - AuthGuard lives INSIDE AppLayout so both the redirect-to-login and the
//     auth-loading <Spinner/> reuse the shell's viewport sizing rules.
//   - react-router v7 `<Outlet />` + nested routes keep the sidebar mounted
//     across navigation (no remount = no lost chat/search state later).

import { lazy, Suspense, type ReactElement } from 'react';
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';

import { Spinner } from '@govard/ui-primitives';

import { AppLayout } from './components/layout/AppLayout.js';
import { AdminGuard } from './shared/auth/AdminGuard.js';
import { AdminDashboardPage } from './features/admin/pages/AdminDashboardPage.js';
import { AdminUsersPage } from './features/admin/pages/AdminUsersPage.js';
import { AdminWorkspacesPage } from './features/admin/pages/AdminWorkspacesPage.js';
import { AdminPlansPage } from './features/admin/pages/AdminPlansPage.js';
import { AdminLlmPage } from './features/admin/pages/AdminLlmPage.js';
import { AdminMailPage } from './features/admin/pages/AdminMailPage.js';
import { AdminAuditPage } from './features/admin/pages/AdminAuditPage.js';
import { AutomationsPage } from './features/automations/pages/AutomationsPage.js';
import RunCenterPage from './features/run-center/pages/RunCenterPage.js';
import { ContactsPage } from './features/contacts/pages/ContactsPage.js';
import { ContactDetailPage } from './features/contacts/pages/ContactDetailPage.js';
import { CopilotLabPage } from './pages/CopilotLabPage.js';
import DashboardPage from './pages/DashboardPage.js';
import { DealCardPage } from './features/deals/pages/DealCardPage.js';
import { DealKanbanPage } from './features/deals/pages/DealKanbanPage.js';
import { HotLeadsPage } from './features/hot-leads/pages/HotLeadsPage.js';
import LoginPage from './pages/LoginPage.js';
import { MemoryBrowserPage } from './features/memory/pages/MemoryBrowserPage.js';
import NotFoundPage from './pages/NotFoundPage.js';
import OperatorModulePage from './pages/OperatorModulePage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import SettingsPage from './pages/SettingsPage.js';
import { SupportPage } from './pages/SupportPage.js';
import { TermsPage } from './pages/TermsPage.js';
import { TemplatesPage } from './features/templates/pages/TemplatesPage.js';
import { ForgotPasswordPage } from './features/account/pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './features/account/pages/ResetPasswordPage.js';
import { VerifyEmailPage } from './features/account/pages/VerifyEmailPage.js';
import { AcceptInvitePage } from './features/team/pages/AcceptInvitePage.js';
import { DocumentsPage } from './features/documents/pages/DocumentsPage.js';
import { DocumentCardPage } from './features/documents/pages/DocumentCardPage.js';
import ImportsPage from './features/imports/pages/ImportsPage.js';
import TasksPage from './features/tasks/pages/TasksPage.js';
import WeekPlannerPage from './features/calendar/pages/WeekPlannerPage.js';
import ProjectsPage from './features/projects/pages/ProjectsPage.js';
import CompaniesPage from './features/companies/pages/CompaniesPage.js';
import NotesPage from './features/notes/pages/NotesPage.js';
import MeetingsPage from './features/meetings/pages/MeetingsPage.js';
import { MeetingCardPage } from './features/meetings/pages/MeetingCardPage.js';
import ApprovalsPage from './features/approvals/pages/ApprovalsPage.js';
import { TaskCardPage } from './features/tasks/pages/TaskCardPage.js';
import { ProjectCardPage } from './features/projects/pages/ProjectCardPage.js';
import ViewsPage from './features/views/pages/ViewsPage.js';
import SavedViewPage from './features/views/pages/SavedViewPage.js';
import { CompanyDetailPage } from './features/companies/pages/CompanyDetailPage.js';
import { TelegramCandidatesPage } from './features/telegram-candidates/pages/TelegramCandidatesPage.js';
import { TelegramOperationsPage } from './features/telegram-operations/pages/TelegramOperationsPage.js';

// The chat workspace is the heaviest route in the shell and is not part of
// the first paint: it is code-split and fetched on demand. The /chat URL and
// behavior stay unchanged.
const ChatPage = lazy(async () => {
  const module = await import('./features/ai-chat/pages/ChatPage.js');
  return { default: module.ChatPage };
});

// Textless loading marker for the lazy chat route: reuses the centered
// Spinner look of the auth guards instead of introducing new copy.
function ChatRouteFallback(): ReactElement {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function RootRedirect(): ReactElement {
  return <Navigate to="/dashboard" replace />;
}

function DocumentCardWrapper(): ReactElement {
  const { documentId } = useParams();
  if (!documentId) return <Navigate to="/documents" replace />;
  return <DocumentCardPage documentId={documentId} />;
}

function TaskCardWrapper(): ReactElement {
  const { taskId } = useParams();
  if (!taskId) return <Navigate to="/tasks" replace />;
  return <TaskCardPage taskId={taskId} />;
}

function MeetingCardWrapper(): ReactElement {
  const { meetingId } = useParams();
  if (!meetingId) return <Navigate to="/meetings" replace />;
  return <MeetingCardPage meetingId={meetingId} />;
}

function ProjectCardWrapper(): ReactElement {
  const { projectId } = useParams();
  if (!projectId) return <Navigate to="/projects" replace />;
  return <ProjectCardPage projectId={projectId} />;
}

function CompanyCardWrapper(): ReactElement {
  const { companyId } = useParams();
  if (!companyId) return <Navigate to="/companies" replace />;
  return <CompanyDetailPage companyId={companyId} />;
}

function DealCardWrapper(): ReactElement {
  const { dealId } = useParams<{ dealId: string }>();
  if (!dealId) return <Navigate to="/deals" replace />;
  return <DealCardPage dealId={dealId} />;
}

function ContactDetailCardWrapper(): ReactElement {
  const { contactId } = useParams<{ contactId: string }>();
  if (!contactId) return <Navigate to="/contacts" replace />;
  return <ContactDetailPage contactId={contactId} />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Вне AuthGuard: человек как раз не может войти, а по ссылке из письма
  // приходят и те, кто ещё не в системе.
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password/:token', element: <ResetPasswordPage /> },
  { path: '/verify-email/:token', element: <VerifyEmailPage /> },
  // Вне AuthGuard: по ссылке приходят и те, у кого аккаунта ещё нет.
  { path: '/invite/:token', element: <AcceptInvitePage /> },
  // Вне AuthGuard: юридические страницы должны читаться до входа — на них
  // ведут ссылки из футера логина и из настроек.
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/support', element: <SupportPage /> },
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <RootRedirect /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/deals', element: <DealKanbanPage /> },
      { path: '/deals/:dealId', element: <DealCardWrapper /> },
      { path: '/tasks', element: <TasksPage /> },
      { path: '/tasks/:taskId', element: <TaskCardWrapper /> },
      { path: '/calendar', element: <WeekPlannerPage /> },
      { path: '/projects', element: <ProjectsPage /> },
      { path: '/projects/:projectId', element: <ProjectCardWrapper /> },
      { path: '/contacts', element: <ContactsPage /> },
      { path: '/contacts/:contactId', element: <ContactDetailCardWrapper /> },
      { path: '/notes', element: <NotesPage /> },
      { path: '/meetings', element: <MeetingsPage /> },
      { path: '/meetings/:meetingId', element: <MeetingCardWrapper /> },
      { path: '/approvals', element: <ApprovalsPage /> },
      { path: '/telegram-candidates', element: <TelegramCandidatesPage /> },
      { path: '/telegram-operations', element: <TelegramOperationsPage /> },
      { path: '/companies', element: <CompaniesPage /> },
      { path: '/companies/:companyId', element: <CompanyCardWrapper /> },
      { path: '/templates', element: <TemplatesPage /> },
      { path: '/documents', element: <DocumentsPage /> },
      {
        path: '/documents/:documentId',
        element: <DocumentCardWrapper />,
      },
      { path: '/imports', element: <ImportsPage /> },
      // The old workflows screen was superseded by /automations; keep the URL
      // working for existing links instead of rendering a deprecated page.
      { path: '/workflows', element: <Navigate to="/automations" replace /> },
      { path: '/hot-leads', element: <HotLeadsPage /> },
      { path: '/views', element: <ViewsPage /> },
      { path: '/views/:viewId', element: <SavedViewPage /> },
      { path: '/memory', element: <MemoryBrowserPage /> },
      {
        path: '/chat',
        element: (
          <Suspense fallback={<ChatRouteFallback />}>
            <ChatPage />
          </Suspense>
        ),
      },
      { path: '/copilot', element: <CopilotLabPage /> },
      { path: '/run-center', element: <RunCenterPage /> },
      { path: '/automations', element: <AutomationsPage /> },
      { path: '/scenario-editor', element: <Navigate to="/automations" replace /> },
      {
        path: '/prompt-compiler',
        element: <OperatorModulePage titleKey="crm.operator.module.prompt_compiler" />,
      },
      {
        path: '/knowledge',
        element: <OperatorModulePage titleKey="crm.operator.module.knowledge" />,
      },
      { path: '/files', element: <OperatorModulePage titleKey="crm.operator.module.files" /> },
      {
        path: '/agents',
        element: <OperatorModulePage titleKey="crm.operator.module.agent_monitor" />,
      },
      {
        path: '/opencode',
        element: <OperatorModulePage titleKey="crm.operator.module.opencode" />,
      },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/team', element: <SettingsPage initialTab="team" /> },
      // Platform admin: same shell (an admin is also a regular user with a
      // workspace), gated by AdminGuard on the nested layout.
      {
        element: <AdminGuard />,
        children: [
          { path: '/admin', element: <AdminDashboardPage /> },
          { path: '/admin/users', element: <AdminUsersPage /> },
          { path: '/admin/workspaces', element: <AdminWorkspacesPage /> },
          { path: '/admin/plans', element: <AdminPlansPage /> },
          { path: '/admin/llm', element: <AdminLlmPage /> },
          { path: '/admin/mail', element: <AdminMailPage /> },
          { path: '/admin/audit', element: <AdminAuditPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
